<template>
  <div v-if="authIsReady">
    <UnifiedHeader />
    <main class="space-y-8">
      <FloatingAiButton />
      <ProfilePanel />
      <div class="bg-white shadow-md rounded-xl p-6">
        <h2 class="text-xl font-semibold text-gray-800">Platform Analytics</h2>
        <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center"><p class="text-sm font-medium text-gray-500">Total Active Clients</p><p v-if="analyticsLoading" class="text-3xl font-bold text-gray-800 animate-pulse">...</p><p v-else class="text-3xl font-bold text-gray-800">{{ clientCount }}</p></div>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center"><p class="text-sm font-medium text-gray-500">Total Standard Users</p><p v-if="analyticsLoading" class="text-3xl font-bold text-gray-800 animate-pulse">...</p><p v-else class="text-3xl font-bold text-gray-800">{{ userCount }}</p></div>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center"><p class="text-sm font-medium text-gray-500">Total Guest Submissions</p><p v-if="analyticsLoading" class="text-3xl font-bold text-gray-800 animate-pulse">...</p><p v-else class="text-3xl font-bold text-gray-800">{{ submissionCount }}</p></div>
        </div>
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div class="bg-white shadow-md rounded-xl p-6 lg:col-span-1">
          <h2 class="text-xl font-semibold text-gray-800">Create New Client</h2>
          <form @submit.prevent="handleCreateClient" class="mt-4 space-y-4">
            <h3 class="text-lg font-medium text-gray-900 border-t pt-4">Podcast Details</h3>
            <div><label for="podcastName" class="block text-sm font-medium text-gray-700">Podcast Name</label><input v-model="form.podcastName" type="text" id="podcastName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-orange focus:border-brand-orange"></div>
            <div><label for="podcastSlogan" class="block text-sm font-medium text-gray-700">Podcast Slogan</label><input v-model="form.podcastSlogan" type="text" id="podcastSlogan" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-orange focus:border-brand-orange"></div>
            <div><label for="podcastLogo" class="block text-sm font-medium text-gray-700">Podcast Logo Image (Optional)</label><input @change="handleFileUpload" type="file" id="podcastLogo" class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-brand-orange hover:file:bg-orange-100"></div>
            <h3 class="text-lg font-medium text-gray-900 border-t pt-4">Client Admin User Details</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label for="firstName" class="block text-sm font-medium text-gray-700">First Name</label><input v-model="form.adminFirstName" type="text" id="firstName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-orange focus:border-brand-orange"></div>
              <div><label for="lastName" class="block text-sm font-medium text-gray-700">Last Name</label><input v-model="form.adminLastName" type="text" id="lastName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-orange focus:border-brand-orange"></div>
            </div>
            <div><label for="adminEmail" class="block text-sm font-medium text-gray-700">Client Admin Email Address</label><input v-model="form.adminEmail" type="email" id="adminEmail" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-orange focus:border-brand-orange"></div>
            <div class="pt-4"><button type="submit" :disabled="isProcessing" class="w-full bg-brand-orange text-white py-2 px-4 rounded-md hover:opacity-90 disabled:opacity-50"><span v-if="isProcessing">Creating Client...</span><span v-else>Create Client and Admin</span></button></div>
          </form>
          <div v-if="feedbackMessage" class="mt-4 p-4 rounded-md text-sm" :class="isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"><p v-html="feedbackMessage"></p></div>
        </div>
        <div class="bg-white shadow-md rounded-xl p-6 lg:col-span-1">
          <div class="flex justify-between items-center"><h2 class="text-xl font-semibold text-gray-800">Manage Existing Clients</h2><input v-model="searchQuery" type="text" placeholder="Search by podcast name..." class="block w-full max-w-xs border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-orange focus:border-brand-orange"></div>
          <div class="mt-4 flow-root"><div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8"><div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"><table class="min-w-full divide-y divide-gray-300"><thead><tr><th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 w-16"><span class="sr-only">Logo</span></th><th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Podcast Name</th><th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Contact Email</th><th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-0"><span class="sr-only">Actions</span></th></tr></thead><tbody class="divide-y divide-gray-200"><tr v-if="clientsLoading"><td colspan="4" class="text-center py-4 text-gray-500">Loading clients...</td></tr><tr v-else-if="filteredClients.length === 0"><td colspan="4" class="text-center py-4 text-gray-500">{{ searchQuery ? 'No clients match your search.' : 'No clients created yet.' }}</td></tr>
            <tr v-else v-for="client in filteredClients" :key="client.id">
              <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-0"><img v-if="client.logoUrl" :src="client.logoUrl" :alt="client.podcastName" class="h-10 w-10 rounded-md object-cover bg-gray-100"><div v-else class="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center text-xs text-gray-400">No Logo</div></td>
              <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">{{ client.podcastName }}</td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ client.primaryContactEmail }}</td>
              <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0"><router-link :to="`/client/${client.id}/edit`" class="text-brand-purple-dark hover:text-brand-pink-static">Edit</router-link><a href="#" @click.prevent="handleRemoveClient(client.id, client.podcastName)" class="ml-4 text-red-600 hover:text-red-800">Remove</a></td>
            </tr>
          </tbody></table></div></div></div>
        </div>
      </div>
    </main>
    <footer class="mt-12 text-center py-4 space-y-4">
      <div class="flex items-center justify-center space-x-2"><img src="@/assets/logo.png" alt="Castive Logo" class="h-6 w-auto opacity-75"><p class="text-sm text-gray-600 font-medium">Powered by Castive</p></div>
      <div class="md:hidden"><a href="https://mannmadecreations.co.uk" target="_blank" rel="noopener noreferrer" class="pixel-perfect-link">GET PIXEL PERFECTION</a></div>
    </footer>
  </div>
</template>

<script>
import { storage, db } from '@/firebase';
import { mapState } from 'vuex';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, query, onSnapshot, orderBy, getCountFromServer, where } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import ProfilePanel from '@/components/ProfilePanel.vue';
import UnifiedHeader from '@/components/UnifiedHeader.vue';
import FloatingAiButton from '@/components/FloatingAiButton.vue';

export default {
  name: 'SuperAdminView',
  components: { ProfilePanel, UnifiedHeader, FloatingAiButton },
  data() {
    return {
      // --- START: DEFINITIVE FORM DATA FIX ---
      form: { podcastName: '', podcastSlogan: '', adminEmail: '', adminFirstName: '', adminLastName: '' },
      // --- END: DEFINITIVE FORM DATA FIX ---
      logoFile: null,
      isProcessing: false,
      feedbackMessage: '',
      isError: false,
      clients: [],
      clientsLoading: true,
      unsubscribeClients: null,
      clientCount: 0,
      userCount: 0,
      submissionCount: 0,
      analyticsLoading: true,
      searchQuery: '',
    };
  },
  computed: {
    ...mapState(['authIsReady']),
    filteredClients() {
      if (!this.searchQuery) {
        return this.clients;
      }
      const lowerCaseQuery = this.searchQuery.toLowerCase();
      // --- START: DEFINITIVE SEARCH FIX ---
      return this.clients.filter(client => {
        return (
          (client.podcastName && client.podcastName.toLowerCase().includes(lowerCaseQuery)) ||
          (client.primaryContactEmail && client.primaryContactEmail.toLowerCase().includes(lowerCaseQuery))
        );
      });
      // --- END: DEFINITIVE SEARCH FIX ---
    }
  },
  methods: {
    handleFileUpload(event) {
      this.logoFile = event.target.files[0] || null;
    },
    async handleCreateClient() {
      this.feedbackMessage = '';
      this.isError = false;
      this.isProcessing = true;
      let logoUrl = null;
      try {
        if (this.logoFile) {
          this.feedbackMessage = "Uploading logo...";
          const fileExtension = this.logoFile.name.split('.').pop();
          const fileName = `client-logos/${uuidv4()}.${fileExtension}`;
          const storageRef = ref(storage, fileName);
          await uploadBytes(storageRef, this.logoFile);
          logoUrl = await getDownloadURL(storageRef);
        }
        this.feedbackMessage = "Creating client and admin user...";
        const functions = getFunctions(undefined, 'europe-west2');
        const createClientAndAdmin = httpsCallable(functions, 'createClientAndAdmin');
        // --- START: DEFINITIVE PAYLOAD FIX ---
        const payload = { 
          podcastName: this.form.podcastName,
          podcastSlogan: this.form.podcastSlogan,
          adminEmail: this.form.adminEmail,
          adminFirstName: this.form.adminFirstName,
          adminLastName: this.form.adminLastName,
          logoUrl: logoUrl 
        };
        const response = await createClientAndAdmin(payload);
        // --- END: DEFINITIVE PAYLOAD FIX ---
        this.feedbackMessage = response.data.result.replace(/\n/g, '<br>');
        this.resetForm();
      } catch (error) {
        console.error("Error in create client process:", error);
        this.feedbackMessage = error.message || "An unknown error occurred.";
        this.isError = true;
      } finally {
        this.isProcessing = false;
      }
    },
    resetForm() {
      // --- START: DEFINITIVE RESET FIX ---
      this.form = { podcastName: '', podcastSlogan: '', adminEmail: '', adminFirstName: '', adminLastName: '' };
      // --- END: DEFINITIVE RESET FIX ---
      this.logoFile = null;
      const podcastLogoInput = document.getElementById('podcastLogo');
      if (podcastLogoInput) {
        podcastLogoInput.value = '';
      }
    },
    fetchClients() {
      const clientsCollection = collection(db, 'clients');
      const q = query(clientsCollection, orderBy('createdAt', 'desc'));
      this.unsubscribeClients = onSnapshot(q, (snapshot) => {
        this.clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        this.clientsLoading = false;
      }, (error) => {
        console.error("Error fetching clients:", error);
        this.clientsLoading = false;
      });
    },
    async handleRemoveClient(clientId, podcastName) {
      if (!confirm(`Are you sure you want to permanently delete the client "${podcastName}" and all of its users? This action cannot be undone.`)) {
        return;
      }
      this.isProcessing = true;
      this.feedbackMessage = `Deleting ${podcastName}...`;
      this.isError = false;
      try {
        const functions = getFunctions(undefined, 'europe-west2');
        const deleteClient = httpsCallable(functions, 'deleteClient');
        const response = await deleteClient({ clientId: clientId });
        this.feedbackMessage = response.data.result;
      } catch (error) {
        console.error("Error calling deleteClient function:", error);
        this.feedbackMessage = error.message || "An unknown error occurred during deletion.";
        this.isError = true;
      } finally {
        this.isProcessing = false;
      }
    },
    async fetchAnalytics() {
      this.analyticsLoading = true;
      try {
        const clientsCol = collection(db, 'clients');
        const usersCol = collection(db, 'users');
        const submissionsCol = collection(db, 'submissions');
        const clientSnapshot = await getCountFromServer(clientsCol);
        const userSnapshot = await getCountFromServer(query(usersCol, where('role', '==', 'user')));
        const submissionSnapshot = await getCountFromServer(submissionsCol);
        this.clientCount = clientSnapshot.data().count;
        this.userCount = userSnapshot.data().count;
        this.submissionCount = submissionSnapshot.data().count;
      } catch (error) {
        console.error("Error fetching analytics data:", error);
        this.feedbackMessage = "Could not load platform analytics.";
        this.isError = true;
      } finally {
        this.analyticsLoading = false;
      }
    }
  },
  created() {
    this.fetchClients();
    this.fetchAnalytics();
  },
  beforeUnmount() {
    if (this.unsubscribeClients) {
      this.unsubscribeClients();
    }
  }
}
</script>